/**
 * Tiny observable localStorage document store.
 *
 * Backs `localBackend` so the whole product (including Game Rooms and
 * their realtime participant list) is developable and demoable without
 * Firebase credentials. Two tabs on the same machine stay in sync through
 * the `storage` event, which is enough to exercise the realtime UI.
 *
 * This is explicitly NOT a Firestore substitute: nothing is shared across
 * devices and nothing is durable. The UI always says so.
 */

const STORAGE_KEY = "idkf.local.db";

const EMPTY_DB = {
  auth: null,
  users: {},
  learning: {}, // uid -> conceptId -> record
  favourites: {}, // uid -> favouriteId -> record
  roomRefs: {}, // uid -> roomId -> record
  rooms: {},
  participants: {}, // roomId -> uid -> participant
  predictions: {}, // roomId -> uid -> prediction
  gameParticipants: {}, // roomId -> gameId -> uid -> participant
  gamePredictions: {}, // roomId -> gameId -> uid -> prediction
  cache: {}, // cacheDocId -> cache entry
};

const listeners = new Set();
let cache = null;

function safeParse(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function read() {
  if (cache) return cache;
  let stored = null;
  try {
    stored = safeParse(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    stored = null;
  }
  cache = { ...EMPTY_DB, ...(stored ?? {}) };
  return cache;
}

function persist(next) {
  cache = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* quota or private mode: in-memory state still works for this session */
  }
  listeners.forEach((listener) => {
    try {
      listener(next);
    } catch {
      /* a broken subscriber must not break the others */
    }
  });
}

/** Read-only snapshot of the whole store. */
export function getState() {
  return read();
}

/**
 * Apply a mutation.
 * @param {(draft: object) => object} mutator receives a shallow clone
 */
export function update(mutator) {
  const next = mutator({ ...read() });
  persist(next);
  return next;
}

/** Subscribe to every change (same tab and, via `storage`, other tabs). */
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Wipe everything, used by "sign out" and by tests. */
export function reset() {
  persist({ ...EMPTY_DB });
}

/** Deep-ish clone so callers can't mutate the store by reference. */
export function clone(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

let crossTabWired = false;
export function wireCrossTabSync() {
  if (crossTabWired || typeof window === "undefined" || !window.addEventListener) {
    return;
  }
  crossTabWired = true;

  window.addEventListener("storage", (event) => {
    if (event.key !== STORAGE_KEY) return;
    cache = { ...EMPTY_DB, ...(safeParse(event.newValue) ?? {}) };
    listeners.forEach((listener) => {
      try {
        listener(cache);
      } catch {
        /* ignore */
      }
    });
  });
}

export const LOCAL_STORE_KEY = STORAGE_KEY;
